import { bell } from "@/constants";
import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverClose } from "@radix-ui/react-popover";
import { PopoverContentV2, PopoverTrigger } from "@/components/ui/popover.tsx";
import useNotificationStore from "@/stores/notification/notification.store.ts";
import { Button } from "@/components/ui/button.tsx";
import { CaretDown, NotificationIcon } from "@/assets/icons/ocean/icons";
import { getCdnUrl } from "@/lib/utils";
import { toast } from "react-toastify";
import {
  useReadAllNotification,
  useReadNotification,
} from "@/services/notification.service";
import { format } from "date-fns";
import { CaretUpIcon } from "@radix-ui/react-icons";

const NotificationDropdownV2 = () => {
  const defaultIcon = "/assets/lobby/notification-gift.png";
  const [isOpen, setIsOpen] = useState(false); // Control popover visibility
  const [isBellOpen, setIsBellOpen] = useState(false); // Control bell visibility
  const [notifications, setNotifications] = useState<any[]>([]);

  const {
    fetchNotifications,
    notifications: fetchedNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();

  const { mutate: read } = useReadNotification();
  const { mutate: readAll } = useReadAllNotification();

  useEffect(() => {
    setNotifications(
      fetchedNotifications.map((n) => {
        return {
          ...n,
          isExpanded: false,
        };
      }),
    );
  }, [fetchedNotifications]);

  const closeRef = useRef<HTMLButtonElement>(null);
  return (
    <Popover
      open={isOpen}
      onOpenChange={(changed: boolean) => {
        setIsOpen(changed);
        setIsBellOpen(changed);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          className="h-[44px] w-[44px] bg-primary-background p-0"
          onClick={() => fetchNotifications()}
          style={{
            filter: isBellOpen ? "brightness(1.3)" : "none",
            transform: isBellOpen ? "scale(1.1)" : "scale(1)",
          }}
        >
          <NotificationIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContentV2
        className={`ocean z-50 ml-4 mr-8 mt-4 overflow-y-auto overflow-x-hidden sm:mr-0`}
      >
        <div className="left-auto w-[400px] items-center rounded-xl bg-[#14142A] text-white sm:absolute sm:right-0 sm:w-96">
          <div
            className="font-base border-b border-white/10 p-4 text-end text-lg text-sm text-white/30 hover:cursor-pointer"
            onClick={() => {
              markAllAsRead();
              readAll();
            }}
          >
            Clear All
          </div>
          <div className="max-h-[400px] space-y-4 overflow-y-auto p-4">
            {notifications.length === 0 ? (
              <div className="text-center text-white/40">No notifications</div>
            ) : (
              notifications?.map((notification) => (
                <div
                  key={notification.id}
                  className="flex flex-col justify-center rounded-lg bg-white/5 p-2 py-4"
                >
                  <div className="flex flex-row items-center">
                    <div className="flex w-[44px] flex-shrink-0 items-center justify-center rounded-full lg:h-[44px]">
                      <img
                        src={notification.icon || defaultIcon}
                        alt="icon"
                        className="h-full w-full"
                      />
                    </div>
                    <div className="ml-3 flex flex-col">
                      <div className="flex items-center justify-between pb-2">
                        <h3 className="font-openSans text-sm font-normal sm:text-base">
                          {notification.title}
                        </h3>
                      </div>
                      <p className="pb-2 font-openSans text-xs font-thin text-white/40 sm:text-sm">
                        {notification.message}
                      </p>
                    </div>
                    <div
                      className="hover:cursor flex w-[38px] flex-shrink-0 items-center justify-center rounded-full hover:cursor-pointer lg:h-[44px]"
                      onClick={() => {
                        markAsRead(notification.id);
                        read(notification.id);
                      }}
                    >
                      <img
                        src={getCdnUrl("assets/notifications/trash.png")}
                        alt="trash-icon"
                        className="w-[26px]"
                      />
                    </div>
                  </div>
                  {notification.isExpanded && (
                    <div className="mt-4 flex flex-col gap-4 p-2 text-sm">
                      {Object.keys(JSON.parse(notification.metadata)).map(
                        (t) => (
                          <div className="flex justify-between">
                            <span className="text-white/40">{t}</span>
                            <div className="w-48 text-wrap break-all text-end">
                              {JSON.parse(notification.metadata)[t]}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                  <div
                    onClick={() => {
                      setNotifications(
                        notifications.map((n) => {
                          if (n.id === notification.id) {
                            return {
                              ...n,
                              isExpanded: !n.isExpanded,
                            };
                          }

                          return {
                            ...n,
                          };
                        }),
                      );
                    }}
                  >
                    {notification.metadata && (
                      <CaretDown
                        className={`mt-4 w-full hover:cursor-pointer ${notification.isExpanded && "rotate-180"}`}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <PopoverClose className="hidden" ref={closeRef} />
      </PopoverContentV2>
    </Popover>
  );
};

export default NotificationDropdownV2;
